import { URLConfig } from '@/config/general.config'
import axios, { AxiosRequestConfig, AxiosRequestHeaders, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import Cookies from 'js-cookie'

const createHttpService = (baseURL: string, withAuth: boolean = true) => {
	const instance = axios.create({
		baseURL,
		headers: {
			'Content-Type': 'application/json'
		}
	})

	if (withAuth) {
		instance.interceptors.request.use(
			(config: InternalAxiosRequestConfig) => {
				// Respect explicit skip auth flag on headers
				if ((config.headers as any)?.__skipAuth) {
					return config
				}

				const token = Cookies.get('userToken') || Cookies.get('access_token')

				if (token) {
					config.headers.Authorization = `Bearer ${token}`
				}
				return config
			},
			(error) => {
				return Promise.reject(error)
			}
		)
	}

	instance.interceptors.response.use(
		(response: AxiosResponse) => {
			return response
		},
		(error) => {
			return Promise.reject(error)
		}
	)

	return instance
}

const httpService = createHttpService(URLConfig.AppUrl as string) // NEXT_PUBLIC_API_URL
const httpServiceBase = createHttpService(URLConfig.AppBaseUrl as string) // NEXT_PUBLIC_API_BASE_URL
const httpServiceMarket = createHttpService(URLConfig.MarketUrl as string) // NEXT_PUBLIC_API_MARKET_URL
const httpServiceGateway = createHttpService('/api/gateway')
type HttpInstance = typeof httpService

const baseApi = async <T>(instance: HttpInstance, url: string, options?: AxiosRequestConfig): Promise<T> => {
	const response: AxiosResponse = await instance(url, options)
	return response.data as T
}

const getData = async <T>(instance: HttpInstance, url: string, headers?: AxiosRequestHeaders): Promise<T> => {
	const options: AxiosRequestConfig = {
		headers: headers,
		method: 'GET'
	}

	return await baseApi<T>(instance, url, options)
}

const postData = async <T, R>(
	instance: HttpInstance,
	url: string,
	data: T,
	headers?: AxiosRequestHeaders
): Promise<R> => {
	const options: AxiosRequestConfig = {
		headers: headers,
		method: 'POST',
		data: JSON.stringify(data)
	}

	return await baseApi<R>(instance, url, options)
}

const putData = async <T, R>(
	instance: HttpInstance,
	url: string,
	data: T,
	headers?: AxiosRequestHeaders
): Promise<R> => {
	const options: AxiosRequestConfig = {
		headers: headers,
		method: 'PUT',
		data: JSON.stringify(data)
	}

	return await baseApi<R>(instance, url, options)
}

const deleteData = async (instance: HttpInstance, url: string, headers?: AxiosRequestHeaders): Promise<void> => {
	const options: AxiosRequestConfig = {
		headers: headers,
		method: 'DELETE'
	}

	return await baseApi(instance, url, options)
}

export { deleteData, getData, httpService, httpServiceBase, httpServiceGateway, httpServiceMarket, postData, putData }
